import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Copy, Printer, Check, Mail, Loader2, ChevronDown, Plus, X, Send, Clock, Users, Bookmark, Trash2, MessageSquare, FileJson } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Loose email validation — good enough to catch typos before we send
// the payload to the backend, which does the strict validation itself.
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

/**
 * CallExportActions — two buttons in the Call Detail header:
 *   1. Copy — copies a rich plain-text summary of the call (info,
 *      grading, coaching, transcript) to the clipboard so the user
 *      can paste into a doc / email.
 *   2. Download PDF — invokes the browser print dialog which offers
 *      "Save as PDF" natively. A print-only stylesheet hides the app
 *      chrome + these buttons and prints just the call detail body.
 *
 * Both operations are pure client-side — no backend endpoint needed.
 */

const fmtDuration = (s) => {
  if (!s && s !== 0) return '—';
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
};

const line = (label, value) => (value == null || value === '' ? '' : `${label}: ${value}\n`);

const bulletList = (heading, items) => {
  const filtered = (items || []).map((x) => (typeof x === 'string' ? x.trim() : x)).filter(Boolean);
  if (!filtered.length) return '';
  return `\n${heading}\n` + filtered.map((x) => `  • ${x}`).join('\n') + '\n';
};

/**
 * Build the rich plain-text export block. Ordered to read like a
 * coaching brief a manager could paste straight into an email.
 */
const buildCopyText = (call, grading) => {
  const parts = [];
  parts.push('═══════════════════════════════════════════════════════');
  parts.push('  YABBIT VOICEFABRIC — CALL DETAIL');
  parts.push('═══════════════════════════════════════════════════════');
  parts.push('');
  parts.push('CALL INFORMATION');
  parts.push('─────────────────────────');
  parts.push(line('Call ID', call?.call_id).trim());
  parts.push(line('Date & Time', fmtDate(call?.call_date)).trim());
  parts.push(line('Agent', call?.agent_name ? `${call.agent_name} (ext ${call.agent_id ?? '—'})` : call?.agent_id).trim());
  parts.push(line('Type', call?.call_type).trim());
  parts.push(line('Direction', call?.call_direction).trim());
  parts.push(line('Duration', fmtDuration(call?.duration_seconds)).trim());
  parts.push(line('From', call?.caller_number || call?.from).trim());
  parts.push(line('To', call?.callee_number || call?.to).trim());
  parts.push(line('Customer', call?.customer_name).trim());
  parts.push(line('Customer Phone', call?.customer_phone).trim());
  parts.push(line('Status', call?.status).trim());
  if (call?.won) {
    parts.push(`WON: yes (source: ${call?.chase_outcome_source || 'manual'}${call?.chase_outcome_by ? ', by ' + call.chase_outcome_by : ''})`);
  }
  parts.push('');

  if (grading) {
    parts.push('GRADING SCORECARD');
    parts.push('─────────────────────────');
    const displayScore = grading.overall_score_adjusted ?? grading.overall_score;
    parts.push(`Overall Score: ${displayScore ?? '—'}/100${
      grading.overall_score_adjusted != null && grading.overall_score_adjusted !== grading.overall_score
        ? `  (raw ${grading.overall_score}, adjusted for closed deal)`
        : ''
    }`);
    if (grading.effective_threshold != null) {
      parts.push(`Escalation Threshold: ${grading.effective_threshold}`);
    }
    if (grading.call_intent) {
      parts.push(`Call Intent: ${grading.call_intent.replace('_', ' ')}${
        grading.prior_calls_used ? ` (${grading.prior_calls_used} prior calls used as context)` : ''
      }`);
    }
    if (grading.call_subtype_label || grading.call_subtype) {
      parts.push(`Detected Sub-intent: ${grading.call_subtype_label || grading.call_subtype}`);
      if (grading.subtype_evidence) parts.push(`  Evidence: "${grading.subtype_evidence}"`);
    }
    if (grading.model_used) parts.push(`Graded By: ${grading.model_used}`);
    parts.push('');

    if (grading.call_summary) {
      parts.push('SUMMARY');
      parts.push('─────────────────────────');
      parts.push(grading.call_summary);
      parts.push('');
    }

    const applicable = (grading.criteria_results || []).filter((c) => !c.not_applicable_for_type);
    const notApplicable = (grading.criteria_results || []).filter((c) => c.not_applicable_for_type);
    if (applicable.length) {
      parts.push('CRITERIA CHECKLIST');
      parts.push('─────────────────────────');
      applicable.forEach((c) => {
        const mark = c.not_needed_for_win ? '○' : c.met ? '✓' : '✗';
        parts.push(`${mark} [w${c.weight ?? 1}] ${c.description}`);
        if (c.evidence && !c.not_needed_for_win) parts.push(`     Evidence: "${c.evidence}"`);
        if (c.not_needed_for_win) parts.push('     (Not needed — deal closed)');
      });
      parts.push('');
    }
    if (notApplicable.length) {
      parts.push('N/A FOR THIS CALL TYPE');
      parts.push('─────────────────────────');
      notApplicable.forEach((c) => {
        const scoped = (c.applies_to || []).map((k) => k.replace('_', ' ')).join(', ') || '—';
        parts.push(`  · ${c.description}   (only scored on: ${scoped})`);
      });
      parts.push('');
    }

    const pos = bulletList('WHAT WENT WELL', grading.positives);
    const neg = bulletList('AREAS TO IMPROVE', grading.negatives);
    const neu = bulletList('NEUTRAL OBSERVATIONS', grading.neutrals);
    const gov = bulletList('⚠ GOVERNANCE VIOLATIONS', grading.governance_violations);
    if (pos) parts.push(pos.trim());
    if (neg) parts.push(neg.trim());
    if (neu) parts.push(neu.trim());
    if (gov) parts.push(gov.trim());

    if (grading.coaching_feedback) {
      parts.push('');
      parts.push('COACHING FEEDBACK');
      parts.push('─────────────────────────');
      parts.push(grading.coaching_feedback);
    }
    const steps = bulletList('RECOMMENDED NEXT STEPS', grading.recommended_next_steps);
    if (steps) parts.push(steps.trim());
    parts.push('');
  }

  if (call?.transcription) {
    parts.push('');
    parts.push('FULL TRANSCRIPT');
    parts.push('─────────────────────────');
    parts.push(call.transcription);
  }

  return parts.filter((p) => p != null).join('\n').replace(/\n{3,}/g, '\n\n');
};

const CallExportActions = ({ call, grading }) => {
  const [copied, setCopied] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // Extra recipients — chip-list of validated addresses the user has
  // added via the "+ CC another address" input on the Email popover.
  // Passed through as `extra_to` on both target=me and target=agent
  // sends, so a manager can CC an SME or client-services person
  // without leaving the page.
  const [extraTo, setExtraTo] = useState([]);
  const [extraInput, setExtraInput] = useState('');
  const [extraInputError, setExtraInputError] = useState('');

  // Directory (domain user list) + recent-CC book — fetched once when the
  // popover opens. Both power the autocomplete dropdown under the CC
  // input. Recent addresses also render as one-tap chips above the input.
  const [directory, setDirectory] = useState([]);
  const [recentCC, setRecentCC] = useState([]);
  // Saved email groups — user-scoped, named CC lists. Powers the
  // one-tap "Groups" chip row and the "Save as group" inline form.
  const [groups, setGroups] = useState([]);
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [directoryLoaded, setDirectoryLoaded] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Email intro presets — canned lead-in blocks the sender can pick
  // ("coaching brief", "great call", etc.). Fetched from the backend
  // the first time the popover opens so we stay in sync with the
  // server-side preset library.
  const [introPresets, setIntroPresets] = useState([]);
  const [introKey, setIntroKey] = useState(''); // '' = no intro
  const [introCustom, setIntroCustom] = useState('');
  const [introPickerOpen, setIntroPickerOpen] = useState(false);

  // Loop-in-now deep link handler: when the Frequent Collaborators
  // widget navigates here with `?cc=<email>&auto_email=1`, pop the
  // Email popover open and pre-add the CC address as a chip. We strip
  // the params from the URL after applying so a browser refresh
  // doesn't re-fire the auto-open.
  useEffect(() => {
    const ccParam = searchParams.get('cc');
    const autoOpen = searchParams.get('auto_email');
    const introParam = searchParams.get('intro');
    if (introParam) {
      setIntroKey(String(introParam).toLowerCase());
    }
    if (!ccParam && !introParam) return;
    // Multiple CCs supported via comma-separated ?cc=a@x,b@x.
    const addresses = ccParam
      ? ccParam.split(',').map((s) => s.trim()).filter(isValidEmail)
      : [];
    if (addresses.length > 0) {
      setExtraTo((prev) => {
        const already = new Set(prev.map((e) => e.toLowerCase()));
        const additions = addresses.filter((a) => !already.has(a.toLowerCase()));
        return [...prev, ...additions].slice(0, 5);
      });
    }
    if (autoOpen === '1' || introParam) {
      setEmailOpen(true);
    }
    // Strip params so a refresh doesn't reopen the popover.
    const next = new URLSearchParams(searchParams);
    next.delete('cc');
    next.delete('auto_email');
    next.delete('intro');
    setSearchParams(next, { replace: true });
    // Intentionally run once on mount — the deep link is a one-shot.
  }, []);

  // Prime the directory + recent-CC + groups lists the first time the
  // popover opens — we don't want to burn requests on every page load.
  // All three endpoints are cheap and cached client-side for the session.
  useEffect(() => {
    if (!emailOpen || directoryLoaded) return;
    Promise.all([
      axios.get(`${API}/users/directory`, { timeout: 10000 })
        .then((r) => r.data?.users || [])
        .catch(() => []),
      axios.get(`${API}/users/recent-cc`, { timeout: 10000 })
        .then((r) => r.data?.recent || [])
        .catch(() => []),
      axios.get(`${API}/users/email-groups`, { timeout: 10000 })
        .then((r) => r.data?.groups || [])
        .catch(() => []),
      axios.get(`${API}/calls/email-intro-presets`, { timeout: 10000 })
        .then((r) => r.data?.presets || [])
        .catch(() => []),
    ]).then(([dir, recent, grps, presets]) => {
      setDirectory(dir);
      setRecentCC(recent);
      setGroups(grps);
      setIntroPresets(presets);
      setDirectoryLoaded(true);
    });
  }, [emailOpen, directoryLoaded]);

  // Autocomplete suggestions: match the current input against
  // directory (name OR email prefix) + recent CCs. Excludes anyone
  // already added as a chip. Capped at 6 rows so the popover stays
  // compact.
  const suggestions = useMemo(() => {
    const q = extraInput.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set(extraTo.map((e) => e.toLowerCase()));
    const rows = [];
    // Recent first — they're the highest-signal picks.
    recentCC.forEach((r) => {
      const e = String(r.email || '').toLowerCase();
      if (!e || seen.has(e)) return;
      if (e.includes(q)) {
        rows.push({ email: r.email, label: '', source: 'recent' });
        seen.add(e);
      }
    });
    // Then directory — prefix + name match.
    directory.forEach((u) => {
      const e = String(u.email || '').toLowerCase();
      const n = String(u.name || '').toLowerCase();
      if (!e || seen.has(e)) return;
      if (e.includes(q) || n.includes(q)) {
        rows.push({
          email: u.email,
          label: u.name || '',
          source: 'directory',
          role: u.role,
          extension: u.extension,
        });
        seen.add(e);
      }
    });
    return rows.slice(0, 6);
  }, [extraInput, directory, recentCC, extraTo]);

  const resetExtras = () => {
    setExtraTo([]);
    setExtraInput('');
    setExtraInputError('');
    setActiveSuggestion(-1);
  };

  const addExtra = (rawValue = null) => {
    const val = String(rawValue ?? extraInput).trim();
    if (!val) return;
    if (!isValidEmail(val)) {
      setExtraInputError('Not a valid email');
      return;
    }
    if (extraTo.some((e) => e.toLowerCase() === val.toLowerCase())) {
      setExtraInputError('Already added');
      return;
    }
    if (extraTo.length >= 5) {
      setExtraInputError('Max 5 extra recipients');
      return;
    }
    setExtraTo((prev) => [...prev, val]);
    setExtraInput('');
    setExtraInputError('');
    setActiveSuggestion(-1);
    // Refocus so the user can chain-add more addresses fast.
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeExtra = (email) => {
    setExtraTo((prev) => prev.filter((e) => e !== email));
  };

  /**
   * One-tap add every address from a saved group. Silently skips any
   * addresses that would push the chip count over 5 or that are already
   * in the current list. Toasts a friendly summary so the manager knows
   * what actually got added.
   */
  const applyGroup = (group) => {
    const already = new Set(extraTo.map((e) => e.toLowerCase()));
    const added = [];
    const skippedExisting = [];
    let slotsLeft = Math.max(0, 5 - extraTo.length);
    (group.addresses || []).forEach((a) => {
      const e = String(a || '').trim();
      if (!e) return;
      if (already.has(e.toLowerCase())) {
        skippedExisting.push(e);
        return;
      }
      if (slotsLeft <= 0) return;
      added.push(e);
      already.add(e.toLowerCase());
      slotsLeft -= 1;
    });
    if (added.length === 0) {
      toast.info(`All addresses from "${group.name}" are already added`);
      return;
    }
    setExtraTo((prev) => [...prev, ...added]);
    setExtraInputError('');
    const skipped = (group.addresses?.length || 0) - added.length;
    toast.success(
      skipped > 0
        ? `Added ${added.length} from "${group.name}" · ${skipped} skipped (already added or slot cap)`
        : `Added ${added.length} from "${group.name}"`,
    );
  };

  /**
   * Save the current chip list as a new named group. Prompts for a
   * name inline via `groupNameInput`. The server enforces uniqueness
   * per-user + max 20 groups + max 25 addresses.
   */
  const saveAsGroup = async () => {
    const name = groupNameInput.trim();
    if (!name) {
      toast.error('Give the group a name first');
      return;
    }
    if (extraTo.length === 0) {
      toast.error('Add at least one address before saving');
      return;
    }
    setSavingGroup(true);
    try {
      const r = await axios.post(
        `${API}/users/email-groups`,
        { name, addresses: extraTo },
        { timeout: 15000 },
      );
      const saved = r.data;
      // Prepend the fresh group so it shows up at the top immediately.
      setGroups((prev) => [saved, ...prev.filter((g) => g.id !== saved.id)]
        .sort((a, b) => a.name.localeCompare(b.name)));
      setGroupNameInput('');
      setGroupFormOpen(false);
      toast.success(`Saved "${saved.name}" · ${saved.addresses.length} address${saved.addresses.length === 1 ? '' : 'es'}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to save group';
      toast.error(detail);
    } finally {
      setSavingGroup(false);
    }
  };

  const deleteGroup = async (group) => {
    // No confirm dialog — small footprint, easy to recreate. If the
    // user regrets it they can just re-add the chips and save again.
    try {
      await axios.delete(`${API}/users/email-groups/${group.id}`, { timeout: 10000 });
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      toast.success(`Deleted "${group.name}"`);
    } catch {
      toast.error('Failed to delete group');
    }
  };

  const sendEmail = async (target) => {
    // Guard: target='custom' requires at least one CC. Otherwise our
    // own frontend validation should have caught it.
    if (target === 'custom' && extraTo.length === 0) {
      setExtraInputError('Add at least one address first');
      return;
    }
    setEmailBusy(true);
    setEmailOpen(false);
    try {
      const body = { target, extra_to: extraTo };
      if (introKey) {
        body.intro = introKey;
        if (introKey === 'custom' && introCustom.trim()) {
          body.intro_custom = introCustom.trim();
        }
      }
      const r = await axios.post(
        `${API}/calls/${call?.call_id}/email-summary`,
        body,
        { timeout: 30000 },
      );
      const recips = r?.data?.recipients || [];
      if (recips.length) {
        toast.success(
          `Emailed to ${recips.slice(0, 2).join(', ')}${recips.length > 2 ? ` +${recips.length - 2}` : ''}`,
        );
      } else {
        toast.info('Email dispatched');
      }
      resetExtras();
      // Refresh the recent-CC list so the addresses we just used bubble
      // to the top of the one-tap chips on the next open.
      if (extraTo.length > 0) {
        axios
          .get(`${API}/users/recent-cc`, { timeout: 10000 })
          .then((r) => setRecentCC(r.data?.recent || []))
          .catch(() => {});
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setEmailBusy(false);
    }
  };

  const handleCopy = async () => {
    const text = buildCopyText(call, grading);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Full call detail copied to clipboard');
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // Fallback for older browsers / non-secure contexts
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Copied to clipboard');
        setCopied(true);
        setTimeout(() => setCopied(false), 2200);
      } catch {
        toast.error('Copy failed — your browser blocked clipboard access');
      }
    }
  };

  // ── vCon export (IETF draft-ietf-vcon-vcon-core, syntax 0.4.0) ──────────
  const [vconOpen, setVconOpen] = useState(false);
  const [vconBusy, setVconBusy] = useState(false);
  const [vconInclude, setVconInclude] = useState({
    transcript: true, grading: true, disc: true, playbook: true, crm_context: true,
  });
  const [vconAudio, setVconAudio] = useState('url');
  const [vconRedact, setVconRedact] = useState(false);
  const [vconSigned, setVconSigned] = useState(false);

  const vconIncCount = Object.values(vconInclude).filter(Boolean).length;

  const downloadVcon = async () => {
    const inc = Object.keys(vconInclude).filter((k) => vconInclude[k]);
    if (!inc.length) { toast.error('Pick at least one thing to include'); return; }
    setVconBusy(true);
    try {
      const r = await axios.get(`${API}/vcon/call/${call.call_id}`, {
        params: { include: inc.join(','), audio: vconAudio, redact: vconRedact, signed: vconSigned },
        responseType: 'blob',
        timeout: 120000,
      });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const cd = r.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `${call.call_id}.vcon.json`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('vCon downloaded');
      setVconOpen(false);
    } catch (err) {
      console.error('vCon export error:', err);
      toast.error('vCon export failed');
    } finally {
      setVconBusy(false);
    }
  };

  const handlePrint = () => {
    // Tag the body so the print stylesheet knows to expose the printable
    // layout. Cleared after the print dialog closes.
    document.body.classList.add('printing-call-detail');
    // Give the browser a tick to apply the class before opening the dialog.
    setTimeout(() => {
      window.print();
      // The `afterprint` event fires on both cancel + save-as-pdf.
      const cleanup = () => {
        document.body.classList.remove('printing-call-detail');
        window.removeEventListener('afterprint', cleanup);
      };
      window.addEventListener('afterprint', cleanup);
      // Belt-and-braces fallback — some browsers don't fire afterprint.
      setTimeout(cleanup, 4000);
    }, 60);
  };

  return (
    <div className="flex items-center gap-2 print:hidden relative" data-testid="call-export-actions">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        data-testid="copy-call-details-btn"
        className="gap-1.5"
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </Button>

      {/* Email split-button — click opens a small popover with "to me"
          and (if the agent has an email on file) "to agent". Closes on
          any option click. */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEmailOpen((v) => !v)}
          disabled={emailBusy || !call?.call_id}
          data-testid="email-call-details-btn"
          className="gap-1.5"
        >
          {emailBusy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Mail className="w-3.5 h-3.5" />
          )}
          Email
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
        {emailOpen && (
          <>
            {/* Click-away catcher */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setEmailOpen(false)}
              aria-hidden="true"
            />
            <div
              className="absolute right-0 top-full mt-1 z-20 w-72 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden"
              data-testid="email-popover"
            >
              <button
                type="button"
                onClick={() => sendEmail('me')}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100"
                data-testid="email-to-me"
              >
                <p className="text-sm font-semibold text-slate-900">Email to me</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {user?.email || 'your address'}
                  {extraTo.length > 0 && (
                    <span className="ml-1 text-teal-700">+ {extraTo.length} CC</span>
                  )}
                </p>
              </button>
              <button
                type="button"
                onClick={() => sendEmail('agent')}
                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100"
                data-testid="email-to-agent"
              >
                <p className="text-sm font-semibold text-slate-900">Email to agent</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {call?.agent_name ? `${call.agent_name} (ext ${call?.agent_id ?? '—'})` : '—'}
                  {extraTo.length > 0 && (
                    <span className="ml-1 text-teal-700">+ {extraTo.length} CC</span>
                  )}
                </p>
              </button>

              {/* Intro preset picker — canned lead-in blocks the sender
                  can drop above the call summary ("coaching brief",
                  "great call", etc.). Optional — no intro by default. */}
              <div className="px-3 pt-2.5 pb-2 border-b border-slate-100 bg-white" data-testid="email-intro-picker">
                <button
                  type="button"
                  onClick={() => setIntroPickerOpen((v) => !v)}
                  className="w-full flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
                  data-testid="email-intro-toggle"
                >
                  <span className="inline-flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    Intro
                    {introKey && (
                      <span className="ml-1 text-teal-700 normal-case tracking-normal">
                        · {introPresets.find((p) => p.key === introKey)?.label
                          || (introKey === 'custom' ? 'Custom' : introKey)}
                      </span>
                    )}
                  </span>
                  <ChevronDown
                    className={`w-3 h-3 opacity-60 transition-transform ${introPickerOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {introPickerOpen && (
                  <div className="mt-2 space-y-1" data-testid="email-intro-options">
                    <button
                      type="button"
                      onClick={() => { setIntroKey(''); setIntroCustom(''); }}
                      className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
                        introKey === ''
                          ? 'bg-slate-100 border-slate-300 text-slate-800 font-semibold'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid="email-intro-option-none"
                    >
                      No intro (just the summary)
                    </button>
                    {introPresets.map((p) => (
                      <button
                        type="button"
                        key={p.key}
                        onClick={() => { setIntroKey(p.key); setIntroCustom(''); }}
                        className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
                          introKey === p.key
                            ? 'bg-teal-50 border-teal-300 text-teal-800 font-semibold'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                        data-testid={`email-intro-option-${p.key}`}
                        title={p.body}
                      >
                        <span className="font-semibold">{p.label}</span>
                        <span className="block text-[10px] font-normal text-slate-500 mt-0.5 line-clamp-2">
                          {p.body}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setIntroKey('custom')}
                      className={`w-full text-left text-xs px-2 py-1.5 rounded border ${
                        introKey === 'custom'
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-800 font-semibold'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid="email-intro-option-custom"
                    >
                      <span className="font-semibold">Custom note</span>
                      <span className="block text-[10px] font-normal text-slate-500 mt-0.5">
                        Write a short lead-in for this specific call.
                      </span>
                    </button>
                    {introKey === 'custom' && (
                      <textarea
                        value={introCustom}
                        onChange={(e) => setIntroCustom(e.target.value.slice(0, 500))}
                        placeholder="Write a short lead-in (max 500 chars)…"
                        rows={3}
                        className="mt-1 w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:border-teal-400 focus:ring-teal-200 resize-none"
                        data-testid="email-intro-custom-input"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Extra recipients — groups row + recent chips (one-tap
                  add) + inline "add another" input with autocomplete
                  dropdown. */}
              <div className="px-3 pt-2.5 pb-3 bg-slate-50/60">
                {/* Groups — one-tap add of every address from a saved
                    named list. Delete via X on hover. Only shown when
                    the user has curated ≥ 1 group. */}
                {groups.length > 0 && !extraInput && (
                  <div className="mb-2" data-testid="email-groups-row">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1">
                      <Users className="w-2.5 h-2.5" /> Groups
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {groups.map((g) => (
                        <span
                          key={g.id}
                          className="group/chip inline-flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-full pl-2 pr-1 py-0.5 text-[11px] font-medium text-indigo-800 transition-colors"
                          data-testid={`email-group-chip-${g.id}`}
                          title={`${g.addresses.length} address${g.addresses.length === 1 ? '' : 'es'} — click to add all\n\n${g.addresses.join('\n')}`}
                        >
                          <button
                            type="button"
                            onClick={() => applyGroup(g)}
                            className="inline-flex items-center gap-1"
                            data-testid={`email-group-apply-${g.id}`}
                          >
                            <Plus className="w-2.5 h-2.5" />
                            {g.name}
                            <span className="text-[9px] text-indigo-500">·{g.addresses.length}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteGroup(g)}
                            className="text-indigo-300 hover:text-rose-600 p-0.5 rounded opacity-0 group-hover/chip:opacity-100 transition-opacity"
                            aria-label={`Delete group ${g.name}`}
                            data-testid={`email-group-delete-${g.id}`}
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent CCs — render as one-tap add chips. Only shown
                    when the user hasn't started typing (avoid clutter
                    once the autocomplete dropdown is visible). */}
                {recentCC.length > 0 && !extraInput && (
                  <div className="mb-2" data-testid="recent-cc-chips">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" /> Recent
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {recentCC
                        .filter((r) => !extraTo.some((e) => e.toLowerCase() === String(r.email).toLowerCase()))
                        .map((r) => (
                          <button
                            key={r.email}
                            type="button"
                            onClick={() => addExtra(r.email)}
                            className="inline-flex items-center gap-1 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-full pl-2 pr-2 py-0.5 text-[11px] font-medium text-teal-800 transition-colors"
                            data-testid={`recent-cc-chip-${r.email}`}
                            title={`Used ${r.count || 1} time${r.count === 1 ? '' : 's'} · click to add`}
                          >
                            <Plus className="w-2.5 h-2.5" />
                            {r.email}
                          </button>
                        ))}
                    </div>
                  </div>
                )}

                {extraTo.length > 0 && (
                  <div
                    className="flex flex-wrap gap-1 mb-2"
                    data-testid="email-extra-chips"
                  >
                    {extraTo.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-2 pr-1 py-0.5 text-[11px] font-medium text-slate-700"
                        data-testid={`email-extra-chip-${email}`}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeExtra(email)}
                          className="text-slate-400 hover:text-rose-600 p-0.5 rounded"
                          aria-label={`Remove ${email}`}
                          data-testid={`email-extra-chip-remove-${email}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-1.5 relative">
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="email"
                      value={extraInput}
                      onChange={(e) => {
                        setExtraInput(e.target.value);
                        setActiveSuggestion(-1);
                        if (extraInputError) setExtraInputError('');
                      }}
                      onKeyDown={(e) => {
                        // Arrow keys navigate the autocomplete list.
                        if (suggestions.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                          e.preventDefault();
                          setActiveSuggestion((prev) => {
                            const dir = e.key === 'ArrowDown' ? 1 : -1;
                            const next = prev + dir;
                            if (next < 0) return suggestions.length - 1;
                            if (next >= suggestions.length) return 0;
                            return next;
                          });
                          return;
                        }
                        // Enter — commit either the highlighted suggestion
                        // or the typed value as a fallback.
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
                            addExtra(suggestions[activeSuggestion].email);
                          } else {
                            addExtra();
                          }
                          return;
                        }
                        // Tab / comma / semicolon commit typed value.
                        if (['Tab', ',', ';'].includes(e.key)) {
                          e.preventDefault();
                          addExtra();
                          return;
                        }
                        // Escape clears the input without closing popover.
                        if (e.key === 'Escape' && extraInput) {
                          e.preventDefault();
                          setExtraInput('');
                          setActiveSuggestion(-1);
                        }
                      }}
                      placeholder={
                        directoryLoaded && directory.length > 0
                          ? 'Type name or email to CC…'
                          : '+ CC another address'
                      }
                      autoComplete="off"
                      className={`w-full text-xs px-2 py-1.5 rounded-md border bg-white focus:outline-none focus:ring-1 ${
                        extraInputError
                          ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-200'
                          : 'border-slate-200 focus:border-teal-400 focus:ring-teal-200'
                      }`}
                      data-testid="email-extra-input"
                    />
                    {/* Autocomplete dropdown — anchored under the input,
                        renders when the user has typed at least one
                        character and we have matches. */}
                    {suggestions.length > 0 && (
                      <div
                        className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden max-h-56 overflow-y-auto"
                        data-testid="email-extra-suggestions"
                      >
                        {suggestions.map((s, i) => (
                          <button
                            type="button"
                            key={s.email}
                            onMouseDown={(e) => {
                              // onMouseDown fires before input blur — keeps
                              // focus on the input after the click.
                              e.preventDefault();
                              addExtra(s.email);
                            }}
                            onMouseEnter={() => setActiveSuggestion(i)}
                            className={`w-full text-left px-2.5 py-1.5 flex items-center justify-between gap-2 border-b border-slate-50 last:border-b-0 ${
                              i === activeSuggestion ? 'bg-teal-50' : 'hover:bg-slate-50'
                            }`}
                            data-testid={`email-extra-suggestion-${s.email}`}
                          >
                            <div className="min-w-0 flex-1">
                              {s.label && (
                                <p className="text-xs font-semibold text-slate-900 truncate">
                                  {s.label}
                                </p>
                              )}
                              <p className={`${s.label ? 'text-[10px] text-slate-500' : 'text-xs text-slate-800'} truncate`}>
                                {s.email}
                              </p>
                            </div>
                            {s.source === 'recent' && (
                              <span className="text-[9px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                                recent
                              </span>
                            )}
                            {s.source === 'directory' && s.role && (
                              <span className="text-[9px] font-medium text-slate-500 flex-shrink-0 capitalize">
                                {String(s.role).replace('_', ' ')}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => addExtra()}
                    disabled={!extraInput.trim()}
                    className="inline-flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 h-7 w-7 disabled:opacity-40"
                    aria-label="Add extra recipient"
                    data-testid="email-extra-add-btn"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                {extraInputError && (
                  <p
                    className="text-[10px] text-rose-600 mt-1"
                    data-testid="email-extra-error"
                  >
                    {extraInputError}
                  </p>
                )}
                {extraTo.length > 0 && (
                  <button
                    type="button"
                    onClick={() => sendEmail('custom')}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-md"
                    data-testid="email-to-custom-only"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Send to CC only ({extraTo.length})
                  </button>
                )}

                {/* Save-as-group inline form — appears once ≥ 2 chips
                    have been added, so the manager isn't tempted to
                    save single-recipient "groups". */}
                {extraTo.length >= 2 && (
                  <div className="mt-2" data-testid="save-group-block">
                    {!groupFormOpen ? (
                      <button
                        type="button"
                        onClick={() => setGroupFormOpen(true)}
                        className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 border border-dashed border-indigo-200 rounded-md px-2 py-1.5"
                        data-testid="save-as-group-btn"
                      >
                        <Bookmark className="w-3 h-3" />
                        Save these {extraTo.length} as a group…
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={groupNameInput}
                          onChange={(e) => setGroupNameInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveAsGroup();
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              setGroupFormOpen(false);
                              setGroupNameInput('');
                            }
                          }}
                          placeholder="Group name (e.g. Sales Coaching Group)"
                          maxLength={60}
                          autoFocus
                          className="flex-1 text-xs px-2 py-1.5 rounded-md border border-indigo-200 bg-white focus:outline-none focus:ring-1 focus:border-indigo-400 focus:ring-indigo-200"
                          data-testid="save-group-name-input"
                        />
                        <button
                          type="button"
                          onClick={saveAsGroup}
                          disabled={savingGroup || !groupNameInput.trim()}
                          className="inline-flex items-center justify-center rounded-md bg-indigo-600 hover:bg-indigo-700 text-white h-7 px-2 text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid="save-group-confirm-btn"
                        >
                          {savingGroup ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setGroupFormOpen(false); setGroupNameInput(''); }}
                          className="inline-flex items-center justify-center rounded-md hover:bg-slate-200 text-slate-500 h-7 w-7"
                          aria-label="Cancel save group"
                          data-testid="save-group-cancel-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={handlePrint}
        data-testid="print-call-details-btn"
        className="gap-1.5"
      >
        <Printer className="w-3.5 h-3.5" />
        Download PDF
      </Button>

      {/* vCon export — spec-compliant conversation container download */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setVconOpen((v) => !v)}
          disabled={vconBusy || !call?.call_id}
          data-testid="vcon-export-btn"
          className="gap-1.5"
        >
          {vconBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileJson className="w-3.5 h-3.5" />}
          vCon
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
        {vconOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setVconOpen(false)} aria-hidden="true" />
            <div
              className="absolute right-0 top-full mt-1 z-20 w-64 rounded-lg border border-slate-200 bg-white shadow-lg p-3"
              data-testid="vcon-popover"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Include in vCon</p>
              {[
                ['transcript', 'Transcript'],
                ['grading', 'Grading report'],
                ['disc', 'DISC / caller insight'],
                ['playbook', 'Callback playbook'],
                ['crm_context', 'CRM context'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 py-1 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={vconInclude[key]}
                    onChange={(e) => setVconInclude((s) => ({ ...s, [key]: e.target.checked }))}
                    data-testid={`vcon-include-${key}`}
                    className="accent-teal-600"
                  />
                  {label}
                </label>
              ))}
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mt-2 mb-1">Recording audio</p>
              <select
                value={vconAudio}
                onChange={(e) => setVconAudio(e.target.value)}
                data-testid="vcon-audio-mode"
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-700 bg-white"
              >
                <option value="none">No audio</option>
                <option value="url">Reference URL + hash</option>
                <option value="inline">Inline base64 (self-contained)</option>
              </select>
              <div className="mt-2 space-y-1">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={vconRedact}
                    onChange={(e) => setVconRedact(e.target.checked)}
                    data-testid="vcon-redact-toggle"
                    className="accent-amber-600"
                  />
                  Redact PII (mask customer phone/name)
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={vconSigned}
                    onChange={(e) => setVconSigned(e.target.checked)}
                    data-testid="vcon-signed-toggle"
                    className="accent-teal-600"
                  />
                  Sign (JWS, tamper-evident)
                </label>
              </div>
              <Button
                size="sm"
                onClick={downloadVcon}
                disabled={vconBusy || vconIncCount === 0}
                data-testid="vcon-download-btn"
                className="w-full mt-3 gap-1.5"
              >
                {vconBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileJson className="w-3.5 h-3.5" />}
                Download .vcon.json
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CallExportActions;
